import React, { useState, useEffect, useRef } from 'react';
import { 
  Github, 
  Linkedin, 
  Mail, 
  ExternalLink, 
  Code2, 
  Layout, 
  Database, 
  Smartphone, 
  Menu, 
  X, 
  ChevronDown,
  ArrowRight,
  Award,
  GraduationCap,
  Calendar,
  Loader2,
  MessageSquare,
  CalendarClock,
  Pill
} from 'lucide-react';
import profileImg from './images/Gemini_Generated_Image_xw0suzxw0suzxw0s.png';
import wpImg1 from './images/WhatsApp Image 2025-11-27 at 02.35.05_a4a21037.jpg';
import wpImg2 from './images/WhatsApp Image 2025-11-27 at 02.36.04_20f4d335.jpg';
import wpImg3 from './images/WhatsApp Image 2025-11-27 at 02.38.46_eccece29.jpg';
import wpImg4 from './images/WhatsApp Image 2025-11-27 at 02.39.47_3c1705a4.jpg';
import recogeImg from './images/recoge.png';
import lemasImg from './images/lemas.jpeg';

// --- Utility Components ---

const RevealOnScroll = ({ children, delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) observer.observe(ref.current);

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-1000 transform ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      {children}
    </div>
  );
};

const Typewriter = ({ text, delay = 100 }) => {
  const [currentText, setCurrentText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setCurrentText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, delay);
      return () => clearTimeout(timeout);
    }
  }, [currentIndex, delay, text]);

  return <span>{currentText}</span>;
};

const ScrollProgress = () => {
  const [scrollWidth, setScrollWidth] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollWidth(progress);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="fixed top-0 left-0 w-full h-1 z-[60]">
      <div 
        className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-100 ease-out"
        style={{ width: `${scrollWidth}%` }}
      />
    </div>
  );
};

// --- UI Components ---

const NavLink = ({ href, children, mobile, onClick }) => (
  <a 
    href={href} 
    onClick={(e) => {
      // Smooth scroll handling
      e.preventDefault();
      if(onClick) onClick();
      const element = document.querySelector(href);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }}
    className={`${
      mobile 
        ? 'block w-full px-4 py-3 text-lg hover:bg-slate-800' 
        : 'text-sm font-medium hover:text-purple-400 transition-colors'
    } text-slate-300 cursor-pointer`}
  >
    {children}
  </a>
);

const SectionTitle = ({ title, subtitle }) => (
  <div className="mb-12 text-center">
    <RevealOnScroll>
      <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{title}</h2>
      <div className="w-20 h-1 bg-purple-500 mx-auto rounded-full mb-4"></div>
      {subtitle && <p className="text-slate-400 max-w-2xl mx-auto">{subtitle}</p>}
    </RevealOnScroll>
  </div>
);

const ProjectCard = ({ title, desc, tags, color, icon: Icon, links, featured, image }) => (
  <div className={`group relative bg-slate-800 rounded-xl overflow-hidden border transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 h-full flex flex-col hover:bg-slate-800/80 ${
    featured 
      ? 'border-purple-500 shadow-[0_0_30px_rgba(168,85,247,0.3)] hover:border-purple-400 hover:shadow-[0_0_40px_rgba(168,85,247,0.5)]' 
      : 'border-slate-700 hover:border-purple-500/50'
  }`}>
    {/* Featured Badge */}
    {featured && (
      <div className="absolute top-4 right-4 z-10 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg animate-pulse">
        ⭐ FEATURED
      </div>
    )}
    
    {/* Image Area */}
    <div className={`${featured ? 'h-64' : 'h-48'} w-full ${!image && color} opacity-80 group-hover:opacity-100 transition-all duration-500 relative flex items-center justify-center overflow-hidden`}>
      {image ? (
        <>
          <img 
            src={image} 
            alt={title}
            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent opacity-70" />
        </>
      ) : (
        <>
          <div className="absolute inset-0 bg-slate-900/20 group-hover:bg-transparent transition-colors" />
          <Icon className={`${featured ? 'w-20 h-20' : 'w-16 h-16'} text-white/70 transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500`} />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent opacity-90" />
        </>
      )}
    </div>
    
    <div className="p-6 flex flex-col flex-grow">
      <h3 className={`${featured ? 'text-2xl' : 'text-xl'} font-bold text-white mb-2 group-hover:text-purple-400 transition-colors`}>
        {title}
      </h3>
      <p className="text-slate-400 mb-4 line-clamp-3 flex-grow group-hover:text-slate-300 transition-colors">
        {desc}
      </p>
      <div className="flex flex-wrap gap-2 mb-6">
        {tags.map((tag, i) => (
          <span key={i} className={`px-2 py-1 text-xs font-medium rounded-md ${
            featured 
              ? 'bg-purple-900/40 text-purple-200 border border-purple-500/50' 
              : 'bg-slate-900 text-purple-300 border border-slate-700'
          }`}>
            {tag}
          </span>
        ))}
      </div>
      <div className="flex items-center gap-4 mt-auto">
        <a 
          href={links.github} 
          target="_blank" 
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-sm text-white hover:text-purple-400 transition-colors"
        >
          <Github size={16} /> Code
        </a>
        {links.demo && links.demo !== '#' && (
          <a 
            href={links.demo} 
            target="_blank" 
            rel="noopener noreferrer"
            className={`flex items-center gap-2 text-sm transition-colors ${
              featured 
                ? 'bg-purple-600 hover:bg-purple-700 px-3 py-1.5 rounded-lg text-white font-medium' 
                : 'text-white hover:text-purple-400'
            }`}
          >
            <ExternalLink size={16} /> Live Demo
          </a>
        )}
      </div>
    </div>
  </div>
);

const TimelineItem = ({ year, title, institution, desc, last }) => (
  <div className="relative pl-8 md:pl-12 py-2 group">
    {/* Line */}
    {!last && (
      <div className="absolute left-[11px] md:left-[15px] top-2 bottom-0 w-0.5 bg-slate-800 group-hover:bg-purple-900/50 transition-colors"></div>
    )}
    
    {/* Dot */}
    <div className="absolute left-[4px] md:left-[8px] top-2 w-4 h-4 rounded-full border-4 border-slate-900 bg-purple-500 group-hover:scale-125 transition-transform duration-300 shadow-[0_0_10px_rgba(168,85,247,0.5)]"></div>
    
    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mb-2">
      <span className="text-purple-400 font-mono text-sm font-bold flex items-center gap-2">
        <Calendar size={14} /> {year}
      </span>
      <h3 className="text-xl font-bold text-white">{title}</h3>
    </div>
    
    <div className="mb-2 text-slate-300 font-medium flex items-center gap-2">
      <GraduationCap size={16} className="text-slate-500"/> {institution}
    </div>
    
    <p className="text-slate-400 text-sm leading-relaxed max-w-2xl">
      {desc}
    </p>
  </div>
);

const SkillBadge = ({ icon: Icon, name }) => (
  <div className="flex flex-col items-center justify-center p-4 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-purple-500/50 hover:bg-slate-800 transition-all duration-300 group hover:-translate-y-2 hover:shadow-[0_0_15px_rgba(168,85,247,0.15)]">
    <Icon className="w-8 h-8 text-slate-400 mb-3 group-hover:text-purple-400 transition-colors transform group-hover:scale-110 duration-300" />
    <span className="text-slate-300 font-medium">{name}</span>
  </div>
);

// --- Data ---

const projectsData = [
  {
    title: "Recoge Clothing System",
    desc: "A collaborative inventory and sales management system built for the Recoge clothing brand.",
    tags: ['React', 'Node.js', 'MERN'],
    color: "bg-purple-600",
    icon: Layout,
    image: recogeImg,
    links: {
      demo: "https://recoge.netlify.app/",
      github: "https://github.com/AdeeshaRanasingha/recoge-website"
    }
  },
  {
    title: "Lemas Travel Ops (Final)",
    desc: "The complete version of a travel operations system designed to handle complex itinerary planning and logistics.",
    tags: ['React', 'Java', 'Spring Boot'],
    color: "bg-blue-600",
    icon: Database,
    image: lemasImg,
    links: {
      demo: "https://lemastravelops.netlify.app/",
      github: "https://github.com/praneesha1516git/Lemas-Travels-Final"
    }
  },
  {
    title: "NextSlot",
    desc: "A modern scheduling and appointment booking application built for efficiency and ease of use.",
    tags: ['Next.js', 'TypeScript', 'PostgreSQL'],
    color: "bg-indigo-600",
    icon: CalendarClock,
    links: {
      demo: "#", 
      github: "https://github.com/AdeeshaRanasingha/NexSlot"
    }
  },
  {
    title: "Pharmacy Management",
    desc: "A comprehensive system for managing pharmacy inventory, sales, and supplier tracking with real-time updates.",
    tags: ['Java', 'MySQL', 'Swing'],
    color: "bg-emerald-600",
    icon: Pill,
    links: {
      demo: "#",
      github: "https://github.com/AdeeshaRanasingha/pharmacy-project"
    }
  },
  {
    title: "WeChat Clone",
    desc: "A real-time messaging application featuring instant text delivery, user authentication, and active status indicators.",
    tags: ['Node.js', 'Socket.io', 'React'],
    color: "bg-green-600",
    icon: MessageSquare,
    links: {
      demo: "#",
      github: "https://github.com/AdeeshaRanasingha/wechat"
    }
  },
  {
    title: "Ceyhope Charity",
    desc: "A dedicated web platform for the Ceyhope non-profit organization to manage events and donations.",
    tags: ['HTML', 'CSS', 'JavaScript'],
    color: "bg-cyan-600",
    icon: Code2,
    links: {
      demo: "#",
      github: "https://github.com/AdeeshaRanasingha/ceyhope"
    }
  }
];

const educationData = [
  {
    year: "2023 - present",
    title: "Bachelor of Information technology sp in Software Engineering",
    institution: "Sri Lanka Institute of Information Technology (SLIIT)",
    desc: "under Graduate with a current GPA of 3.86. Specialized in Software Engineering."
  },
  {
    year: "2020 - 2022",
    title: "GCE AL - Mathematics Stream",
    institution: "Ananda college collombo",
    desc: "Focus on Mathematics, Physics, and chemestry. Achived 3 cs . was acordinator of photographic art society.was an active member in science uinion as well as school ict society"
  },
  {
    year: "2014 - 2019",
    title: "GCE OL - 8As",
    institution: "Royal Institiute International School",
    desc: "got 8As including mathematics and science subjects. actively participated in extracurricular activities such as debating, drama, and sports.and was a junior prefect"
  }
];

const galleryImages = [
  { src: wpImg1, caption: 'Project proposal' },
  { src: wpImg2, caption: 'Dean list awards' },
  { src: wpImg3, caption: 'coding life' },
  { src: wpImg4, caption: 'working setup' }
];

export default function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  // Form State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  // Handle Scroll for Navbar styling
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'About', href: '#about' },
    { name: 'Education', href: '#education' },
    { name: 'Skills', href: '#skills' },
    { name: 'Projects', href: '#projects' },
    { name: 'Gallery', href: '#gallery' },
    { name: 'Contact', href: '#contact' },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch("https://formsubmit.co/ajax/adeeshaharshana@gmail.com", {
        method: "POST",
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message,
          _subject: `New Portfolio Message from ${formData.name}`
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' }); // Clear form
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-200 font-sans selection:bg-purple-500/30 overflow-x-hidden">
      
      {/* Custom Keyframes for Animations */}
      <style>{`
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
          100% { transform: translateY(0px); }
        }
        @keyframes shine {
          0% { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-shine {
          background: linear-gradient(to right, #a855f7 20%, #ec4899 40%, #a855f7 60%, #ec4899 80%);
          background-size: 200% auto;
          color: transparent;
          -webkit-background-clip: text;
          background-clip: text;
          animation: shine 5s linear infinite;
        }
      `}</style>

      <ScrollProgress />

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-slate-900/90 backdrop-blur-md border-b border-slate-800 py-4 shadow-lg' : 'bg-transparent py-6'}`}>
        <div className="container mx-auto px-6 flex justify-between items-center">
          <a href="/" className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-400 hover:animate-shine transition-all">
            Adeesha.lk
          </a>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <NavLink key={link.name} href={link.href}>{link.name}</NavLink>
            ))}
            <a href="#contact" className="px-5 py-2 text-sm font-medium text-white bg-purple-600 rounded-full hover:bg-purple-700 transition-colors shadow-lg shadow-purple-900/20 transform hover:-translate-y-0.5 inline-block text-center hover:shadow-purple-500/25 active:scale-95 duration-200">
              Hire Me
            </a>
          </div>

          {/* Mobile Toggle */}
          <button 
            className="md:hidden text-slate-300 hover:text-white"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-slate-900 border-b border-slate-800 animate-in fade-in slide-in-from-top-5">
            <div className="flex flex-col p-4">
              {navLinks.map((link) => (
                <NavLink 
                  key={link.name} 
                  href={link.href} 
                  mobile 
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.name}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        {/* Background Gradients */}
        <div className="absolute top-20 right-0 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl -z-10 animate-pulse duration-[3000ms]"></div>
        <div className="absolute bottom-20 left-0 w-72 h-72 bg-blue-600/10 rounded-full blur-3xl -z-10 animate-pulse duration-[4000ms]"></div>

        <div className="container mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-6 animate-in slide-in-from-left-10 duration-1000 fade-in">
            <RevealOnScroll>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 border border-slate-700 text-purple-400 text-sm font-medium mb-4">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                Available for Freelance
              </div>
              <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight mb-4">
                Building <br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500">
                  <Typewriter text="Digital Ideas" delay={150} />
                </span>
                <span className="text-purple-400 animate-pulse">_</span>
              </h1>
              <p className="text-lg text-slate-400 max-w-lg leading-relaxed mb-6">
                I'm a Full Stack Developer passionate about building accessible, pixel-perfect, and performant web experiences.
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <a href="#projects" className="px-8 py-3 bg-purple-600 text-white rounded-full font-medium hover:bg-purple-700 transition-all hover:scale-105 shadow-lg shadow-purple-900/25 flex items-center gap-2 active:scale-95">
                  View Work <ArrowRight size={18} />
                </a>
                <a href="#contact" className="px-8 py-3 bg-transparent border border-slate-700 text-white rounded-full font-medium hover:bg-slate-800 transition-all hover:scale-105 active:scale-95">
                  Contact Me
                </a>
              </div>
            </RevealOnScroll>
            
            <RevealOnScroll delay={200}>
              <div className="flex items-center gap-6 pt-8 text-slate-500">
                <Github className="hover:text-white transition-colors cursor-pointer transform hover:scale-125 duration-200" />
                <Linkedin className="hover:text-white transition-colors cursor-pointer transform hover:scale-125 duration-200" />
                <Mail className="hover:text-white transition-colors cursor-pointer transform hover:scale-125 duration-200" />
              </div>
            </RevealOnScroll>
          </div>

          {/* Hero Visual */}
          <div className="relative hidden md:block animate-float">
            <div className="relative z-10 bg-slate-800/90 backdrop-blur-sm border border-slate-700 rounded-2xl p-6 shadow-2xl rotate-3 hover:rotate-0 transition-transform duration-500 hover:shadow-purple-500/20">
              <div className="flex items-center gap-2 mb-4 border-b border-slate-700 pb-4">
                <div className="w-3 h-3 rounded-full bg-red-500 hover:scale-110 transition-transform"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500 hover:scale-110 transition-transform"></div>
                <div className="w-3 h-3 rounded-full bg-green-500 hover:scale-110 transition-transform"></div>
              </div>
              <div className="space-y-3 font-mono text-sm">
                <div className="flex gap-2">
                  <span className="text-pink-500">const</span>
                  <span className="text-blue-400">developer</span>
                  <span className="text-white">=</span>
                  <span className="text-yellow-300">{`{`}</span>
                </div>
                <div className="pl-6 flex gap-2">
                  <span className="text-purple-400">name:</span>
                  <span className="text-green-400">'Adeesha'</span>,
                </div>
                <div className="pl-6 flex gap-2">
                  <span className="text-purple-400">skills:</span>
                  <span className="text-yellow-300">['React', 'Next.js', 'Node']</span>,
                </div>
                <div className="pl-6 flex gap-2">
                  <span className="text-purple-400">gpa:</span>
                  <span className="text-orange-400">3.86</span>,
                </div>
                <div className="pl-6 flex gap-2">
                  <span className="text-purple-400">smatWorker:</span>
                  <span className="text-red-400">true</span>
                </div>
                <div className="text-yellow-300">{`}`}</div>
              </div>
            </div>
            {/* Decorative background element behind code block */}
            <div className="absolute inset-0 bg-gradient-to-tr from-purple-600 to-pink-600 rounded-2xl blur-xl opacity-20 -z-10 transform rotate-6 scale-105 animate-pulse"></div>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce text-slate-500">
          <ChevronDown size={24} />
        </div>
      </section>

      {/* About Me Section */}
      <section id="about" className="py-24 bg-slate-950">
        <div className="container mx-auto px-6">
          <SectionTitle 
            title="About Me" 
            subtitle="Get to know the person behind the code."
          />
          
          <div className="grid md:grid-cols-2 gap-16 items-center">
            {/* Image Side */}
            <RevealOnScroll>
              <div className="relative group">
                <div className="absolute inset-0 bg-purple-600 rounded-2xl rotate-6 group-hover:rotate-3 transition-transform duration-300 opacity-50 blur-lg animate-pulse"></div>
                <div className="relative rounded-2xl overflow-hidden border-2 border-slate-700 shadow-2xl">
                  {/* Profile Image (Gemini generated) */}
                  <img 
                    src={profileImg}
                    alt="Profile" 
                    className="w-full h-full object-cover aspect-[3/4] hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
            </RevealOnScroll>

            {/* Content Side */}
            <div className="space-y-8">
              <RevealOnScroll delay={200}>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Driven by <span className="text-purple-400">Performance</span> & <span className="text-pink-400">Design</span>
                  </h3>
                  <p className="text-slate-400 leading-relaxed text-lg">
                    I'm a dedicated developer who thrives on solving complex problems. 
                    My academic journey has been defined by excellence, maintaining a high standard 
                    in every project I undertake. I specialize in building full-stack applications 
                    that are not only functional but also deliver a premium user experience.
                  </p>
                </div>
              </RevealOnScroll>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-4">
                <RevealOnScroll delay={300}>
                  <div className="bg-slate-900 p-4 rounded-xl border border-slate-800 hover:border-purple-500/50 transition-colors group">
                    <div className="flex items-center gap-3 mb-2">
                      <GraduationCap className="text-purple-400 group-hover:scale-110 transition-transform" size={24} />
                      <span className="text-white font-bold text-xl">3.86 GPA</span>
                    </div>
                    <p className="text-sm text-slate-500">Academic Excellence</p>
                  </div>
                </RevealOnScroll>
                
                <RevealOnScroll delay={400}>
                  <div className="bg-slate-900 p-4 rounded-xl border border-slate-800 hover:border-purple-500/50 transition-colors group">
                    <div className="flex items-center gap-3 mb-2">
                      <Award className="text-pink-400 group-hover:scale-110 transition-transform" size={24} />
                      <span className="text-white font-bold text-xl">Top 5%</span>
                    </div>
                    <p className="text-sm text-slate-500">Dean's List Honoree</p>
                  </div>
                </RevealOnScroll>
              </div>

              {/* Achievements List */}
              <RevealOnScroll delay={500}>
                <div className="bg-slate-900/50 p-6 rounded-xl border border-slate-800">
                  <h4 className="text-white font-bold mb-4 flex items-center gap-2">
                    <Award size={18} className="text-yellow-400" /> Key Achievements
                  </h4>
                  <ul className="space-y-3 text-slate-400 text-sm">
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-2"></div>
                      <span>1st year 1st sem deans list awards</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-2"></div>
                      <span>1st year 2nd sem deans list awards</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-2"></div>
                      <span>2nd year 1st sem deans list awards</span>
                    </li>
                  </ul>
                </div>
              </RevealOnScroll>

              {/* Current Stack */}
              <RevealOnScroll delay={600}>
                <div>
                  <h4 className="text-white font-bold mb-3 text-sm uppercase tracking-wider text-slate-500">
                    Current Tech Stack Focus
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {['Next.js 14', 'TypeScript', 'Tailwind CSS', 'Node.js', 'PostgreSQL', 'Docker'].map((tech) => (
                      <span key={tech} className="px-3 py-1 bg-purple-900/30 text-purple-300 rounded-full text-sm border border-purple-500/20 hover:bg-purple-900/50 transition-colors cursor-default hover:scale-105 duration-200">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* Education Timeline Section */}
      <section id="education" className="py-24 bg-slate-900">
        <div className="container mx-auto px-6">
          <SectionTitle 
            title="Education & Experience" 
            subtitle="My academic journey and professional milestones."
          />
          
          <div className="max-w-3xl mx-auto">
            <div className="relative">
                {educationData.map((item, index) => (
                  <RevealOnScroll key={index} delay={index * 200}>
                    <TimelineItem 
                      {...item} 
                      last={index === educationData.length - 1} 
                    />
                  </RevealOnScroll>
                ))}
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-24 bg-slate-900/50">
        <div className="container mx-auto px-6">
          <SectionTitle 
            title="Technical Skills" 
            subtitle="I work with modern technologies to build scalable and robust applications."
          />
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {[
              { icon: Code2, name: "React" },
              { icon: Layout, name: "Tailwind" },
              { icon: Database, name: "Node.js" },
              { icon: Smartphone, name: "Responsive" },
              { icon: Github, name: "Git" },
              { icon: Code2, name: "TypeScript" }
            ].map((skill, index) => (
              <RevealOnScroll key={index} delay={index * 100}>
                <SkillBadge icon={skill.icon} name={skill.name} />
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-24 relative">
        <div className="container mx-auto px-6">
          <SectionTitle 
            title="Featured Projects" 
            subtitle="A collection of applications demonstrating my full-stack capabilities."
          />

          {/* Featured Projects (First 2) */}
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {projectsData.slice(0, 2).map((project, index) => (
              <RevealOnScroll key={index} delay={index * 150}>
                <ProjectCard {...project} featured />
              </RevealOnScroll>
            ))}
          </div>

          {/* Other Projects */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projectsData.slice(2).map((project, index) => (
              <RevealOnScroll key={index} delay={(index + 2) * 150}>
                <ProjectCard {...project} />
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-24 bg-slate-900">
        <div className="container mx-auto px-6">
          <SectionTitle 
            title="Gallery" 
            subtitle="Snapshots from my journey in tech and beyond."
          />
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {galleryImages.map((img, index) => (
              <RevealOnScroll key={index} delay={index * 100}>
                <div className="relative group overflow-hidden rounded-xl aspect-[4/3] cursor-pointer">
                  <img 
                    src={img.src} 
                    alt={img.caption}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                    <span className="text-white font-medium transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                      {img.caption}
                    </span>
                  </div>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-slate-800/30">
        <div className="container mx-auto px-6">
          <RevealOnScroll>
            <div className="max-w-4xl mx-auto bg-slate-800 rounded-2xl p-8 md:p-12 shadow-2xl border border-slate-700 hover:border-purple-500/50 transition-colors duration-500">
              <div className="grid md:grid-cols-2 gap-12">
                <div>
                  <h2 className="text-3xl font-bold text-white mb-6">Let's work together</h2>
                  <p className="text-slate-400 mb-8">
                    Have a project in mind? I'm currently available for freelance work and open to full-time opportunities.
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 text-slate-300 group">
                      <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-purple-400 group-hover:bg-purple-600 group-hover:text-white transition-all duration-300 group-hover:scale-110">
                        <Mail size={20} />
                      </div>
                      <span className="group-hover:text-white transition-colors">adeeshaharshana@gmail.com</span>
                    </div>
                    <div className="flex items-center gap-4 text-slate-300 group">
                      <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-purple-400 group-hover:bg-purple-600 group-hover:text-white transition-all duration-300 group-hover:scale-110">
                        <Github size={20} />
                      </div>
                      <span className="group-hover:text-white transition-colors">github.com/AdeeshaRanasingha</span>
                    </div>
                    <div className="flex items-center gap-4 text-slate-300 group">
                      <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-purple-400 group-hover:bg-purple-600 group-hover:text-white transition-all duration-300 group-hover:scale-110">
                        <Linkedin size={20} />
                      </div>
                      <span className="group-hover:text-white transition-colors">linkedin.com/in/AdeeshaRanasingha</span>
                    </div>
                  </div>
                </div>

                <form className="space-y-4" onSubmit={handleSubmit}>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-1">Name</label>
                    <input 
                      type="text" 
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all focus:bg-slate-900/80" 
                      placeholder="John Doe" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-1">Email</label>
                    <input 
                      type="email" 
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all focus:bg-slate-900/80" 
                      placeholder="john@example.com" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-1">Message</label>
                    <textarea 
                      name="message"
                      rows="4" 
                      required
                      value={formData.message}
                      onChange={handleInputChange}
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all focus:bg-slate-900/80" 
                      placeholder="Tell me about your project..."
                    ></textarea>
                  </div>
                  
                  <button 
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium py-3 rounded-lg hover:opacity-90 transition-opacity shadow-lg shadow-purple-900/30 hover:scale-[1.02] transform duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="animate-spin" size={20} />
                        Sending...
                      </>
                    ) : (
                      "Send Message"
                    )}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="p-3 bg-green-500/10 border border-green-500/20 text-green-400 rounded-lg text-sm text-center animate-in fade-in slide-in-from-bottom-2">
                      Message sent successfully! I'll get back to you soon.
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg text-sm text-center animate-in fade-in slide-in-from-bottom-2">
                      Failed to send message. Please try again.
                    </div>
                  )}
                </form>
              </div>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 py-8 border-t border-slate-900">
        <div className="container mx-auto px-6 text-center">
          <p className="text-slate-500">© 2024 Alex.dev. All rights reserved.</p>
          <div className="flex justify-center gap-6 mt-4">
            <a href="#!" className="text-slate-500 hover:text-purple-400 transition-colors">Privacy Policy</a>
            <a href="#!" className="text-slate-500 hover:text-purple-400 transition-colors">Terms of Service</a>
          </div>
        </div>
      </footer>
    </div>
  );
}